import React, { useContext, useEffect, useState } from "react";

import { faChartMixed, faCog, faPerson, faTrophy } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useGetPainGraphData } from "api/hooks/useGetPainGraphData";
import { useGetPatientFeelingReports } from "api/hooks/useGetPatientFeelingReports";
import { useGetPatientGoalsQuery } from "api/hooks/useGetPatientGoalsQuery";
import { useGetPatientHealthJournalQuery } from "api/hooks/useGetPatientHealthJournalQuery";
import { useGetPhysicalFunctionGraphData } from "api/hooks/useGetPhysicalFunctionGraphData";
import type { Goal } from "api/models/Goal";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { PatientGoal } from "shared/molecules/PatientGoal";
import { CurrentPatientContext } from "utils/contexts";
import { hasTreatmentFeature } from "utils/features";
import { userIsTherapist } from "utils/profile/profileHelper";

import { isGoalUndefined } from "../Information/views/Goal";
import { NonEditGoal } from "../Information/views/NonEditGoal";

import { AdherenceChart } from "./components/AdherenceChart";
import { Chart } from "./components/Chart";
import { useChartData } from "./components/Chart/useChartData";
import { ChartToolbar } from "./components/ChartToolbar/ChartToolbar";
import { Overview } from "./components/Overview";
import { PlanOfCareCertificationContainer } from "./components/PlanOfCareCertificationBox/PlanOfCareCertificationContainer";

export const PatientOverview: React.FC = () => {
  const { patientId } = useParams<Record<string, string>>();
  const { t } = useTranslation();
  const printRef = React.useRef(null);
  const notSet = t("dashboard.tables.calls.goal.not_set");
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const { profile } = useProfileContext();
  const isTherapist = userIsTherapist(profile);

  const [editGoal, setEditGoal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [goal, setGoal] = useState<Partial<Goal>>();

  const { data: healthJournal } = useGetPatientHealthJournalQuery({
    patientId: patient.id,
    treatment_id: previousTreatmentId,
  });

  const enableFeelingChart =
    healthJournal?.ailment && hasTreatmentFeature({ ailment: healthJournal.ailment, feature: "feelingChart" });
  const enablePainChart =
    healthJournal?.ailment && hasTreatmentFeature({ ailment: healthJournal.ailment, feature: "painChart" });
  const enableFunctionChart =
    healthJournal?.ailment && hasTreatmentFeature({ ailment: healthJournal.ailment, feature: "functionChart" });
  const enablePainLocation =
    healthJournal?.ailment && hasTreatmentFeature({ ailment: healthJournal.ailment, feature: "painLocation" });

  const physicalFunctionResponse = useGetPhysicalFunctionGraphData(
    { patientId: patient.id, treatment_id: previousTreatmentId },
    { enabled: Boolean(enableFunctionChart) }
  );
  const painResponse = useGetPainGraphData(
    { patientId: patient.id, treatment_id: previousTreatmentId },
    { enabled: Boolean(enablePainChart) }
  );
  const getPatientGoalsQuery = useGetPatientGoalsQuery({ patientId: patient.id, treatment_id: previousTreatmentId });
  const feelingResponse = useGetPatientFeelingReports(
    { patientID: patient.id, treatment_id: previousTreatmentId },
    { enabled: Boolean(enableFeelingChart) }
  );

  const painChartData = useChartData({ type: "pain", painResponse });
  const functionChartData = useChartData({ type: "physical-function", physicalFunctionResponse });
  const feelingChartData = useChartData({ type: "feeling", feelingResponse });

  useEffect(() => {
    if (getPatientGoalsQuery.data?.[0]) {
      setGoal({
        id: getPatientGoalsQuery.data[0].id,
        free_text: getPatientGoalsQuery.data[0].free_text,
        pain: getPatientGoalsQuery.data[0].pain,
        functionality: getPatientGoalsQuery.data[0].functionality,
        feeling: getPatientGoalsQuery.data[0].feeling,
      });
    }
  }, [getPatientGoalsQuery.data]);

  const successFunction = ({ id, free_text, pain, functionality, feeling }: Partial<Goal>) => {
    setSuccess(true);
    setGoal({ id, free_text, pain, functionality, feeling });
    setEditGoal(false);
    setTimeout(() => setSuccess(false), 3000);
  };

  const goalUndefined = isGoalUndefined(goal);

  const isPhysicalFunctionAvailable = () => {
    const mostPainfulLocation = patient.most_painful_location?.location;
    switch (mostPainfulLocation) {
      case "hand":
      case "shoulder":
      case "neck":
        return false;
      default:
        return true;
    }
  };

  // should really be healthJournal?.ailment === "joint_pain" but lets use location to be backwards compatible
  const isPainAvailable = () => patient.most_painful_location?.location;

  const isFeelingAvailable = () =>
    healthJournal?.ailment === "osteoporosis" || healthJournal?.ailment === "fall_prevention";

  return (
    <OuterContainer>
      <POCContainer>
        <PlanOfCareCertificationContainer patientId={patient?.id} />
      </POCContainer>

      <Container ref={printRef}>
        {enableFeelingChart && (
          <WidgetBox
            icon={<FontAwesomeIcon icon={faCog} />}
            title={t("patients.graph.general_feeling")}
            description={t("common.week_num", { number: feelingChartData.currentWeek })}
            color="blue"
            bordered
            toolbar={
              <ChartToolbar
                currentValue={feelingChartData.currentValue}
                sinceStart={feelingChartData.generateSinceStart()}
                chartType="feeling"
                hasData={feelingChartData.graphData.length > 0}
                currentLabel={t("patients.graph.current")}
                sinceStartLabel={t("patients.graph.since_start")}
              />
            }
          >
            <Chart feelingResponse={feelingResponse} type="feeling" goal={goal?.feeling} />
          </WidgetBox>
        )}

        {enablePainChart && isPainAvailable() && (
          <WidgetBox
            icon={<FontAwesomeIcon icon={faChartMixed} />}
            title={t("patients.pain")}
            description={t("common.week_num", { number: painChartData.currentWeek })}
            color="purple"
            bordered
            toolbar={
              <ChartToolbar
                currentValue={painChartData.currentValue}
                sinceStart={painChartData.generateSinceStart()}
                chartType="pain"
                hasData={painChartData.graphData.length > 0}
                currentLabel={t("patients.graph.current")}
                sinceStartLabel={t("patients.graph.since_start")}
              />
            }
          >
            <Chart painResponse={painResponse} type="pain" goal={goal?.pain} />
          </WidgetBox>
        )}

        {enableFunctionChart && isPhysicalFunctionAvailable() && (
          <WidgetBox
            icon={<FontAwesomeIcon icon={faChartMixed} />}
            title={t("partner_patients.function")}
            description={t("common.week_num", { number: functionChartData.currentWeek })}
            color="yellow"
            bordered
            toolbar={
              <ChartToolbar
                currentValue={functionChartData.currentValue}
                sinceStart={functionChartData.generateSinceStart()}
                chartType="physical-function"
                hasData={functionChartData.graphData.length > 0}
                currentLabel={t("patients.graph.current")}
                sinceStartLabel={t("patients.graph.since_start")}
              />
            }
          >
            <Chart
              physicalFunctionResponse={physicalFunctionResponse}
              type="physical-function"
              goal={goal?.functionality}
            />
          </WidgetBox>
        )}

        <AdherenceChart />

        {enablePainLocation && (
          <WidgetBox icon={<FontAwesomeIcon icon={faPerson} />} title={t("patients.your_pain")} color="blue" bordered>
            <Overview />
          </WidgetBox>
        )}

        <WidgetBox
          icon={<FontAwesomeIcon icon={faTrophy} />}
          title={t("patients.your_goal")}
          color="green"
          bordered
          toolbar={
            editGoal || !isTherapist || getPatientGoalsQuery.error ? undefined : (
              <BaseButton
                dataTestID="add-new-goal-button"
                variant="action"
                icon="pen-to-square"
                text={goalUndefined ? t("buttons.add_new_goal") : t("buttons.edit_goal")}
                onClick={() => setEditGoal(true)}
              />
            )
          }
        >
          {editGoal ? (
            <div data-testid="edit-goal-field">
              <PatientGoal
                id={Number(patientId)}
                onCancel={() => setEditGoal(false)}
                onSuccess={successFunction}
                currentGoal={{
                  id: goal?.id,
                  description: goal?.free_text,
                  functionality: goal?.functionality?.toString(),
                  pain: goal?.pain?.toString(),
                  feeling: goal?.feeling?.toString(),
                }}
                isPhysicalFunctionAvailable={isPhysicalFunctionAvailable}
                isFeelingAvailable={isFeelingAvailable}
                isPainAvailable={isPainAvailable}
              />{" "}
            </div>
          ) : (
            <div>
              <NonEditGoal
                canDeleteGoal={isTherapist && !goalUndefined}
                success={success}
                setGoal={setGoal}
                patient={patient}
                patientId={patientId ?? ""}
                goal={goal}
                notSet={notSet}
                error={getPatientGoalsQuery.error}
                isPhysicalFunctionAvailable={isPhysicalFunctionAvailable}
                isFeelingAvailable={isFeelingAvailable}
                isPainAvailable={isPainAvailable}
              />
            </div>
          )}
        </WidgetBox>
      </Container>
    </OuterContainer>
  );
};

const POCContainer = styled.div``;

const OuterContainer = styled.div`
  padding: 0px 16px 16px;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 16px;
  ${props => props.theme.belowLargeBreakpoint} {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;
